import { TaskComponents } from "Components/SentenceTask";
import { DEFAULT_VOICES, STATIC_URL, STORY_TASKS, TaskTypes } from "App.constants";
import cx from "classnames";
import { LessonType, SentenceType, TaskType } from "App.types";
import StoryTask from "Components/SentenceTask/StoryTask";
import { Button, Image } from "antd";
import { ExportOutlined, ZoomInOutlined } from "@ant-design/icons";
import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { encodeS3URI, isImageUrl, replaceTemplates } from "App.helpers";
import { UserContext } from "App";
import { useNavigate, useParams } from "react-router-dom";
import * as rdd from "react-device-detect";
import styles from "./LessonReview.module.scss";
import { useSuspenseQuery } from "@tanstack/react-query";
import API from "Api";
import useSentenceAudio from "Hooks/useSentenceAudio";
import useTextTemplatesTooltip from "Hooks/useTextTemplatesTooltip";
import { isMobile } from "react-device-detect";
import VideoPlayer from "Components/SentenceTask/VideoPlayer";

const LessonReviewTask: FC<{
  idx: number;
  activeSentence: SentenceType;
  lesson: LessonType;
  sentences: SentenceType[];
  storyId: number;
  allTasks: any[];
}> = ({ allTasks, sentences, activeSentence, lesson, idx }) => {
  const [audioIndex, setAudioIndex] = useState(0);

  const { id = "", course } = useParams();

  const navigate = useNavigate();

  const user = useContext(UserContext);
  const { data: userProps } = useSuspenseQuery({
    queryKey: ["props", user?.id],
    queryFn: API.user.getProps,
    staleTime: Infinity,
  });

  const TaskComponent = activeSentence?.task && TaskComponents[activeSentence.task];

  const audio1 = useSentenceAudio({ sentence: activeSentence });
  const audio2 = useSentenceAudio({ sentence: activeSentence, voice: DEFAULT_VOICES[1] });
  const audios = useMemo(() => [audio1, audio2].filter((a) => a), [audio1, audio2]);

  useEffect(() => {
    audio1?.addEventListener("play", () => audio2?.pause());
    audio2?.addEventListener("play", () => audio1?.pause());

    const setNextIdx = () => setAudioIndex((prev) => (prev === 1 ? 0 : prev + 1));

    audios.forEach((audio) => {
      (audio as HTMLAudioElement).addEventListener("ended", setNextIdx);
      // (audio as HTMLAudioElement).addEventListener("pause", setNextIdx);
    });
  }, [activeSentence, audio1, audio2, audios]);

  const title = useMemo(
    () =>
      replaceTemplates(activeSentence?.description, userProps) ||
      activeSentence?.grammar?.title ||
      TaskTypes[activeSentence?.task || ""]?.desc,
    [activeSentence?.description, activeSentence?.grammar?.title, activeSentence?.task, userProps],
  );

  const renderText = useTextTemplatesTooltip();

  const mediaComponent = () => {
    const mediaAudio = activeSentence?.markers.includes("onlyAudio") && activeSentence?.mediaStory && (
      <audio
        controls
        src={`${STATIC_URL}/stories/${encodeS3URI(activeSentence.mediaStory.title)}/${activeSentence?.mediaStory?.audio || "audio.mp3"}`}
      />
    );

    const audio = activeSentence?.media?.includes(".mp3") && (
      <audio controls src={`${STATIC_URL}/media/${encodeS3URI(activeSentence.media)}`} />
    );

    const image = isImageUrl(activeSentence?.media) && !isMobile && (
      <Image
        preview={
          !isMobile && {
            toolbarRender: () => null,
            mask: <ZoomInOutlined style={{ fontSize: 24 }} />,
            maskClassName: styles.mask,
          }
        }
        rootClassName={styles.childrenImage}
        onError={(e: any) => (e.target.style.visibility = "hidden")}
        src={`${STATIC_URL}/media/${encodeS3URI(activeSentence?.media)}`}
        // style={{ backgroundImage: `url(${STATIC_URL}/media/${activeSentence.media})` }}
        alt={""}
        height={"100%"}
      />
    );
    return mediaAudio || audio || image ? (
      <>
        {mediaAudio}
        {audio}
        {image}
      </>
    ) : null;
  };

  return (
    <div className={styles.task}>
      <div className={styles.lesson}>
        <Button
          type={"text"}
          size={"small"}
          onClick={() => {
            // @ts-ignore
            rdd.isMobile = false;
            navigate(`/course/${course}/lessons/exercise/${id}/${idx}`, { state: { allTasks } });
          }}
          className={styles.taskType}
        >
          <ExportOutlined /> {activeSentence?.task}
        </Button>

        {!!(activeSentence?.medias?.length || activeSentence?.media?.toLowerCase().includes(".mp4")) &&
          //@ts-ignore
          ![TaskType.Video, TaskType.VideoSelect, TaskType.VideoDragDrop].includes(activeSentence.task) && (
            <VideoPlayer
              transcripts={activeSentence?.transcripts}
              withFullscreen={true}
              className={styles.video}
              largeCaptionSize
              showCaptions={activeSentence.markers.includes("subtitles") || activeSentence.markers.includes("subtitlesAfter")}
              medias={
                activeSentence.medias.filter((m) => m.meta !== "draft").length
                  ? activeSentence.medias
                  : [
                      {
                        url: activeSentence.media,
                      },
                    ]
              }
              autoPlay={false}
            />
          )}

        {title && activeSentence?.task !== TaskType.Intro && <div className={styles.title}>{renderText(title, styles.tooltip)}</div>}

        <div
          className={cx(styles.content, {
            [styles.content__fullHeight]:
              lesson.story.type === "dialog" || activeSentence?.task === "grammar" || activeSentence?.linkedStoryId,
          })}
        >
          {activeSentence &&
            ((activeSentence?.task && STORY_TASKS.includes(activeSentence.task)) ||
            lesson.story.type === "dialog" ||
            (activeSentence.linkedStoryId && activeSentence.task === TaskType.Video) ? (
              <StoryTask
                mediaStory={activeSentence.mediaStory}
                activeType={activeSentence.task}
                story={activeSentence.linkedStory || lesson.story}
                lesson={lesson}
                onTaskComplete={() => ({})}
                onNext={() => ({})}
                media={activeSentence.media}
                markers={activeSentence.markers}
                autoPlay={false}
              />
            ) : (
              <TaskComponent
                audio={audios?.[audioIndex]}
                sentence={activeSentence}
                activeType={activeSentence?.task}
                onTaskComplete={() => new Promise(() => {})}
                lesson={lesson}
                setDictOpened={() => {}}
                showComments={() => {}}
                setTry={() => {}}
                onNext={() => {}}
                // showGrammar={lesson.grammar && setGrammarOpened}
                alignCenter
                showAnswer={activeSentence?.task === TaskType.SpellerListen}
                sentences={sentences}
                autoPlay={false}
                showSuccessMessage
              >
                {mediaComponent()}
              </TaskComponent>
            ))}
        </div>
      </div>
    </div>
  );
};
export default LessonReviewTask;
